import Cookies from "js-cookie";
import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { MessageTypeList, UserAlertsDto } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { UpgradeProfitOptimizationDialog } from "@src/components/Dialogs/UpgradeProfitOptimizationDialog";
import { ViewAs } from "@src/components/Navigation/ViewAs/ViewAs";
import { StandardRoutes } from "@src/components/Utils/routes";
import { initOrganization } from "@src/store/slices/organization";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { switchOrganization } from "../api/organizations/switch-organization";
import { useGetOrgList } from "../api/organizations-list/get-organization-list";
import { useUser } from "../api/user-auths/login";
import { ScopedDialog } from "../components/Dialogs/ScopedDialog";
import { SetDomainDialog } from "../components/Dialogs/SetDomainDialog";
import { usePrefetchQueries } from "../hooks/prefetchQueries";
import { useQueryParam } from "../hooks/util";
import { appendNewOrg } from "../store/slices/appSettings";
import { setDialog } from "../store/slices/global";
import { Logout } from "../utils";
import { IgStorage } from "../utils/igStorage";
import Loading from "../utils/Loading";

import { DialogProvider } from "./DialogProvider";
import { WebsocketProvider } from "./WebsocketProvider";

interface LoggedInProviderProps {
  children?: ReactNode;
}
export const LoggedInProvider = ({ children }: LoggedInProviderProps) => {
  usePrefetchQueries();

  const dispatch = useDispatch();
  const organization = useGetOrg();
  const user = useUser();
  const orgList = useGetOrgList();
  const location = useLocation();
  const navigate = useNavigate();
  const liveChatEnabled = useConfiguration<boolean>("live_chat_enabled")?.value;
  const userAlerts = useMemo(() => {
    let userAlerts: Partial<UserAlertsDto> = {};
    if (user.data?.type === "login" && user.data.user.userAlerts) {
      userAlerts = {
        ...user.data.user.userAlerts,
      };
    }
    return userAlerts;
  }, [user.isSuccess]);

  const viewedAnalyticsReleasePopup = useMemo(
    () => Cookies.get("viewedAnalyticsReleaseDialog") === "true",
    []
  );

  const isAdmin = useIsAdmin();
  const widgetAuth = useQueryParam("widgetAuth");
  const userPopup = useQueryParam("userPopup");
  const organizationId = useQueryParam("orgId");
  const shopifyDomain = useQueryParam("shopId");
  const logoutWorker = useRef<SharedWorker | null>(null);

  useEffect(() => {
    try {
      if (!logoutWorker.current) {
        logoutWorker.current = new SharedWorker("./workers/logout-worker.js", {
          type: "module",
          name: "Global Logout",
        });
      }
    } catch (e) {
      console.log(e);
    }

    if (logoutWorker.current) {
      logoutWorker.current.port.onmessage = () => {
        Logout();
      };
    } else if (!widgetAuth) {
      Logout();
    }
  }, []);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      const type = e.data?.type;
      if (!MessageTypeList.includes(type)) {
        return null;
      }

      if (type === "LOG_OUT") {
        logoutWorker.current?.port.postMessage({ test: "test" });
      }
    },
    [user.isLoading, user.data]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isAdmin && (event.metaKey || event.ctrlKey)) {
        if (event.key === "s") {
          event.preventDefault();
          void navigate(StandardRoutes().settings.path);
        }
      }
    },
    [navigate, isAdmin]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (
      organization.data.org.shopId !== null &&
      organization.data.org.name !== null
    ) {
      dispatch(
        appendNewOrg({
          shopId: organization.data.org.shopId!,
          name: organization.data.org.name,
        })
      );
    }
  }, [dispatch, organization.data.org.shopId, organization.data.org.name]);

  useEffect(() => {
    if (user.isSuccess && user.data.type === "login") {
      if (user.data.user.showWelcome) {
        dispatch(setDialog({ dialog: "onboarding", open: true }));
      }

      // else if (
      //   (isAdmin && userPopup) ||
      //   (isAdmin &&
      //     userAlerts.showAnalyticsReleaseDialog !== false &&
      //     !viewedAnalyticsReleasePopup)
      // ) {
      //   dispatch(setDialog({ dialog: "showUserInfoPopup", open: true }));
      // }
    }
  }, [
    organization.isSuccess,
    userAlerts,
    user.isSuccess,
    isAdmin,
    viewedAnalyticsReleasePopup,
    user.data,
  ]);

  useEffect(() => {
    if (user.isSuccess && user.data && user.data.type === "login") {
      const usInterval = setInterval(() => {
        if (
          window.Usersnap &&
          user.data &&
          user.isSuccess &&
          user.data.type === "login"
        ) {
          window.Usersnap.init({
            user: { email: user.data.user.email },
          });
          clearInterval(usInterval);
        }
      }, 100);
    }
  }, [user.data, user.isSuccess]);

  useEffect(() => {
    if (
      user.isSuccess &&
      user.data &&
      user.data.type === "login" &&
      organization.data &&
      organization.isSuccess
    ) {
      window.pylon = {
        chat_settings: {
          app_id: "67b7d8b3-1e45-4ed0-8349-213619079471",
          email: user.data.user.email,
          name: user.data.user.firstName + " " + user.data.user.lastName,
        },
      };
      window.Pylon("setTicketFormFields", {
        brand: organization.data.org.shopId,
      });
    }
    if (liveChatEnabled) {
      const usInterval = setInterval(() => {
        if (
          window.Usersnap &&
          user.data &&
          user.isSuccess &&
          user.data.type === "login"
        ) {
          window.Usersnap.init({
            user: { email: user.data.user.email, plan: "live_chat" },
          });
          clearInterval(usInterval);
        }
      }, 100);
    }
  }, [user.data, user.isSuccess, organization.data, liveChatEnabled]);

  useEffect(() => {
    dispatch(initOrganization(organization.data.org));
  }, [organization.data.org]);

  useEffect(() => {
    if (
      isAdmin &&
      user.isSuccess &&
      organization.isSuccess &&
      orgList.isSuccess &&
      (organizationId || shopifyDomain)
    ) {
      const currentOrgId = organization.data.org.id;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("orgId");
      searchParams.delete("shopId");
      const cleanSearch = searchParams.toString();
      const targetRoute =
        location.pathname + (cleanSearch ? `?${cleanSearch}` : "");

      let targetOrg: any = null;

      if (organizationId) {
        targetOrg = orgList.data.orgs.find((org) => org.id === organizationId);
      } else if (shopifyDomain) {
        targetOrg = orgList.data.orgs.find(
          (org) => org.shopId === shopifyDomain
        );
      }

      if (targetOrg && targetOrg.id !== currentOrgId) {
        const token = IgStorage.authToken;
        if (token) {
          void switchOrganization(token, targetOrg.id).then(() => {
            const cleanUrl = targetRoute || location.pathname;
            void navigate(cleanUrl, { replace: true });
            window.location.reload();
          });
        }
      } else if ((organizationId || shopifyDomain) && !targetOrg) {
        console.error(
          "Organization not found or access denied:",
          organizationId || shopifyDomain
        );
        const cleanUrl = targetRoute || location.pathname;
        void navigate(cleanUrl, { replace: true });
      } else if (organizationId || shopifyDomain) {
        const cleanUrl = targetRoute || location.pathname;
        void navigate(cleanUrl, { replace: true });
      }
    }
  }, [
    isAdmin,
    user.isSuccess,
    organization.isSuccess,
    orgList.isSuccess,
    organizationId,
    shopifyDomain,
    organization.data?.org?.id,
    location.pathname,
    location.search,
    navigate,
    orgList.data?.orgs,
  ]);

  return (
    <Box>
      <ViewAs />
      <WebsocketProvider />
      <ScopedDialog />
      <SetDomainDialog />
      <UpgradeProfitOptimizationDialog />
      <QueryErrorResetBoundary>
        <Suspense fallback={<Loading />}>
          <DialogProvider />
          {children ? children : <Outlet />}
        </Suspense>
      </QueryErrorResetBoundary>
    </Box>
  );
};

export default LoggedInProvider;
